import React, { Component, ErrorInfo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

export class TestReporter extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // If in test mode, notify parent shell about the crash
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('test_mode') === 'true') {
      console.error('Test Smoke Failure Handled by Reporter:', error, errorInfo);
      window.parent.postMessage({ 
        type: 'TEST_ERROR', 
        message: `React Crash: ${error.message}` 
      }, window.location.origin);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-center text-white">
          <div className="w-20 h-20 bg-red-500/10 border border-red-500/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
             <span className="text-3xl text-red-500">⚠</span>
          </div>
          <h1 className="text-2xl font-black mb-2">Something went wrong</h1>
          <p className="text-slate-400 mb-6 font-mono text-sm max-w-md mx-auto">{this.state.errorMessage}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-white/5 border border-white/10 rounded-2xl font-bold hover:bg-white/10"
          >
            Reload App
          </button>
        </div>
      );
    }

    return <LoadSuccessNotifier>{this.props.children}</LoadSuccessNotifier>;
  }
}

function LoadSuccessNotifier({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isTestMode = searchParams.get('test_mode') === 'true';

  useEffect(() => {
    if (isTestMode) {
      // Add a small delay to ensure rendering is complete and no async crashes happen immediately
      const timer = setTimeout(() => {
        window.parent.postMessage({ type: 'TEST_LOADED' }, window.location.origin);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isTestMode]);

  return <>{children}</>;
}
